import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMXN } from '../../services/roomData';
import { isConverted, getCurrency } from '../../services/currencyService';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Stack,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Avatar,
  useMediaQuery,
  Tooltip,
  Fade,
  Backdrop,
  Slide,
  Grow,
  Zoom,
  Menu,
  MenuItem,
  keyframes,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Bed as BedIcon,
  Groups as GroupsIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { checkMultipleAvailability } from '../../services/bookingService';

/* ============ date parsing (duplicated to keep component self-contained) ============ */

function parseSpanishMonth(text) {
  const months = {
    enero: 0, febrero: 1, marzo: 2, abril: 3, mayo: 4, junio: 5,
    julio: 6, agosto: 7, septiembre: 8, octubre: 9, noviembre: 10, diciembre: 11,
    ene: 0, feb: 1, mar: 2, abr: 3, may: 4, jun: 5,
    jul: 6, ago: 7, sep: 8, oct: 9, nov: 10, dic: 11,
  };
  return months[text.toLowerCase().trim()] ?? null;
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateNice(ymd) {
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

function parseDatesFromText(text) {
  const lower = text.toLowerCase().replace(/,/g, '').trim();
  const now = new Date();
  const currentYear = now.getFullYear();

  let m = lower.match(/(\d{4})-(\d{1,2})-(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return { checkIn: `${m[1]}-${m[2].padStart(2,'0')}-${m[3].padStart(2,'0')}`, checkOut: `${m[4]}-${m[5].padStart(2,'0')}-${m[6].padStart(2,'0')}` };

  m = lower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*(?:al|a|hasta|-)\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return { checkIn: `${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`, checkOut: `${m[6]}-${m[5].padStart(2,'0')}-${m[4].padStart(2,'0')}` };

  m = lower.match(/(?:del?\s+)?(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) { const mo = parseSpanishMonth(m[3]); if (mo !== null) { const y = m[4] ? parseInt(m[4]) : (mo >= now.getMonth() ? currentYear : currentYear+1); return { checkIn: toYMD(new Date(y,mo,parseInt(m[1]))), checkOut: toYMD(new Date(y,mo,parseInt(m[2]))) }; } }

  m = lower.match(/(?:del?\s+)?(\d{1,2})\s+(?:de\s+)?(\w+)\s+(?:al|a|hasta)\s+(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) { const m1=parseSpanishMonth(m[2]),m2=parseSpanishMonth(m[4]); if(m1!==null&&m2!==null){ const y=m[5]?parseInt(m[5]):(m1>=now.getMonth()?currentYear:currentYear+1); return {checkIn:toYMD(new Date(y,m1,parseInt(m[1]))),checkOut:toYMD(new Date(y,m2,parseInt(m[3])))}; } }

  m = lower.match(/(\d{1,2})\s+(\w+)\s+(?:al|a|hasta)\s+(\d{1,2})\s+(\w+)(?:\s+(\d{4}))?/);
  if (m) { const m1=parseSpanishMonth(m[2]),m2=parseSpanishMonth(m[4]); if(m1!==null&&m2!==null){ const y=m[5]?parseInt(m[5]):(m1>=now.getMonth()?currentYear:currentYear+1); return {checkIn:toYMD(new Date(y,m1,parseInt(m[1]))),checkOut:toYMD(new Date(y,m2,parseInt(m[3])))}; } }

  m = lower.match(/(\w+)\s+(\d{1,2})\s*(?:al|a|hasta|-)\s*(\d{1,2})(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) { const mo=parseSpanishMonth(m[1]); if(mo!==null){ const y=m[4]?parseInt(m[4]):(mo>=now.getMonth()?currentYear:currentYear+1); return {checkIn:toYMD(new Date(y,mo,parseInt(m[2]))),checkOut:toYMD(new Date(y,mo,parseInt(m[3])))}; } }

  m = lower.match(/(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?\s+(\d+)\s*noches?/);
  if (m) { const mo=parseSpanishMonth(m[2]); if(mo!==null){ const y=m[3]?parseInt(m[3]):(mo>=now.getMonth()?currentYear:currentYear+1); const ci=new Date(y,mo,parseInt(m[1])); const co=new Date(ci); co.setDate(co.getDate()+parseInt(m[4])); return {checkIn:toYMD(ci),checkOut:toYMD(co)}; } }

  m = lower.match(/(\d{1,2})\s+(?:de\s+)?(\w+)\s+(\d+)\s*noches?/);
  if (m) { const mo=parseSpanishMonth(m[2]); if(mo!==null){ const y=mo>=now.getMonth()?currentYear:currentYear+1; const ci=new Date(y,mo,parseInt(m[1])); const co=new Date(ci); co.setDate(co.getDate()+parseInt(m[3])); return {checkIn:toYMD(ci),checkOut:toYMD(co)}; } }

  // Single date → 1 noche
  m = lower.match(/(?:para\s+)?(?:el\s+)?(\d{1,2})\s+(?:de\s+)?(\w+)(?:\s+(?:de\s+)?(\d{4}))?/);
  if (m) { const mo=parseSpanishMonth(m[2]); if(mo!==null){ const y=m[3]?parseInt(m[3]):(mo>now.getMonth()||(mo===now.getMonth()&&parseInt(m[1])>=now.getDate())?currentYear:currentYear+1); const ci=new Date(y,mo,parseInt(m[1])); const co=new Date(ci); co.setDate(co.getDate()+1); return {checkIn:toYMD(ci),checkOut:toYMD(co)}; } }

  m = lower.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) { const ci=new Date(parseInt(m[1]),parseInt(m[2])-1,parseInt(m[3])); const co=new Date(ci); co.setDate(co.getDate()+1); return {checkIn:toYMD(ci),checkOut:toYMD(co)}; }

  m = lower.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) { const ci=new Date(parseInt(m[3]),parseInt(m[2])-1,parseInt(m[1])); const co=new Date(ci); co.setDate(co.getDate()+1); return {checkIn:toYMD(ci),checkOut:toYMD(co)}; }

  return null;
}

function parseLugarFromText(text) {
  const lower = text.toLowerCase();
  if (/boutique/i.test(lower)) return 'boutique';
  if (/casa\s*hotel|casahotel/i.test(lower)) return 'casaHotel';
  return null;
}

function parseNavigationIntent(text) {
  const lower = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Skip navigation if the message contains dates (user wants availability, not navigation)
  if (/\d{1,2}\s+(de\s+)?\w+/.test(lower) && parseDatesFromText(text)) return null;
  const nav = /(llevame|ir\s*a|vamos\s*a|quiero\s*(ir|ver)|go\s*to|take\s*me|voir|aller|gehen|zeig)/i.test(lower);
  if (/\b(boda|bodas|wedding|weddings|hochzeit|mariage|nuptials)\b/.test(lower)) return { path: '/bodas', label: 'Bodas' };
  if (/\b(boutique)\b/.test(lower)) return { path: '/boutique', label: 'Hotel Boutique' };
  if (/\b(casa\s*hotel|casahotel)\b/.test(lower)) return { path: '/reservas', label: 'Casa Hotel' };
  if (nav && /\b(reserva|reservas|reservations?)\b/.test(lower)) return { path: '/reservas', label: 'Reservas' };
  if (/\b(inicio|home|accueil|startseite)\b/.test(lower)) return { path: '/', label: 'Inicio' };
  return null;
}

function calculateNights(checkIn, checkOut) {
  const [y1,m1,d1] = checkIn.split('-').map(Number);
  const [y2,m2,d2] = checkOut.split('-').map(Number);
  return Math.round((new Date(y2,m2-1,d2) - new Date(y1,m1-1,d1)) / 86400000);
}

/* ============ Animations ============ */

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(212,175,55,.5); }
  70% { box-shadow: 0 0 0 18px rgba(212,175,55,0); }
  100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

/* ============ Welcome overlay key ============ */
const OVERLAY_KEY = 'lacapilla_chatbot_seen';

const LANGS = [
  { code: 'es', flag: '🇲🇽', label: 'Español' },
  { code: 'en', flag: '🇺🇸', label: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
];

/* ============ Component ============ */

export default function FloatingChatBot() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const WELCOME_MSG_CONTENT = t('chatbot.welcome');
  const [open, setOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [messages, setMessages] = useState([{ role: 'bot', content: WELCOME_MSG_CONTENT }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [langAnchor, setLangAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentLang = LANGS.find(l => l.code === i18n.language?.substring(0, 2)) || LANGS[0];

  // Show overlay on first visit
  useEffect(() => {
    if (!localStorage.getItem(OVERLAY_KEY)) {
      const timer = setTimeout(() => setShowOverlay(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptOverlay = () => {
    setShowOverlay(false);
    localStorage.setItem(OVERLAY_KEY, '1');
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const addMessage = (msg) => setMessages(prev => [...prev, msg]);

  /* ─── send logic ─── */
  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    addMessage({ role: 'user', content: text });

    /* ── navigation intent ── */
    const navIntent = parseNavigationIntent(text);
    if (navIntent) {
      addMessage({ role: 'bot', content: t('chatbot.navigating', { page: navIntent.label }), navPath: navIntent.path });
      setTimeout(() => { navigate(navIntent.path); setOpen(false); }, 1500);
      return;
    }

    const dates = parseDatesFromText(text);
    if (!dates) {
      addMessage({ role: 'bot', content: t('chatbot.noDate') });
      return;
    }

    const nights = calculateNights(dates.checkIn, dates.checkOut);
    if (nights <= 0) { addMessage({ role: 'bot', content: t('chatbot.badDate') }); return; }

    const lugar = parseLugarFromText(text);
    const lugarLabel = lugar === 'boutique' ? 'Hotel Boutique' : lugar === 'casaHotel' ? 'Casa Hotel' : t('chatbot.bothHotels');

    addMessage({ role: 'bot', content: t('chatbot.searching', { hotel: lugarLabel, checkIn: formatDateNice(dates.checkIn), checkOut: formatDateNice(dates.checkOut), nights: `${nights}` }), loading: true });
    setLoading(true);

    try {
      const result = await checkMultipleAvailability(dates.checkIn, dates.checkOut, lugar);
      setMessages(prev => prev.filter(m => !m.loading));

      if (result.error) { addMessage({ role: 'bot', content: `❌ ${result.error}` }); return; }

      const available = (result.rooms || []).filter(r => r.isAvailable);
      const unavailable = (result.rooms || []).filter(r => !r.isAvailable);

      if (available.length === 0) {
        const noRoomsKey = lugar ? 'chatbot.noRooms' : 'chatbot.noRoomsBoth';
        addMessage({ role: 'bot', content: t(noRoomsKey, { hotel: lugarLabel }) });
      } else {
        addMessage({ role: 'bot', content: t('chatbot.found', { count: `${available.length}` }), rooms: available, unavailableRooms: unavailable, dates, nights });
      }
    } catch {
      setMessages(prev => prev.filter(m => !m.loading));
      addMessage({ role: 'bot', content: t('chatbot.error') });
    } finally { setLoading(false); }
  };

  const getBookingUrl = (room, dates) => {
    const path = room.lugar === 'boutique' ? '/boutique' : '/reservas';
    return `${path}?checkIn=${dates.checkIn}&checkOut=${dates.checkOut}&room=${encodeURIComponent(room.roomName)}`;
  };

  /* ─── render message ─── */
  const renderMessage = (msg, idx) => {
    const isBot = msg.role === 'bot';
    return (
      <Box key={idx} sx={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end', mb: 1.5, gap: 0.8, alignItems: 'flex-start' }}>
        {isBot && <Avatar sx={{ bgcolor: '#d4af37', width: 28, height: 28, mt: 0.3 }}><BotIcon sx={{ fontSize: 16 }} /></Avatar>}
        <Box sx={{ maxWidth: '82%', minWidth: 0 }}>
          <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: isBot ? '0 12px 12px 12px' : '12px 0 12px 12px', bgcolor: isBot ? '#f5f5f5' : '#d4af37', color: isBot ? '#1a1a1a' : '#fff', border: isBot ? '1px solid #e8e8e8' : 'none' }}>
            {msg.content.split('\n').map((line, i) => (
              <Typography key={i} variant="body2" sx={{ mb: line === '' ? 0.6 : 0.15, lineHeight: 1.45, fontSize: '0.82rem' }}
                dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>') }} />
            ))}
            {msg.loading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.5 }}><CircularProgress size={14} sx={{ color: '#d4af37' }} /><Typography variant="caption" color="text.secondary">{t('chatbot.consulting')}</Typography></Box>}
          </Paper>

          {/* Room cards */}
          {msg.rooms && msg.rooms.length > 0 && (
            <Stack spacing={0.8} sx={{ mt: 1 }}>
              {msg.rooms.map((room, ri) => (
                <Card key={ri} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #c8e6c9', bgcolor: '#f1f8e9' }}>
                  <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ minWidth: 0 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                        <Typography variant="caption" fontWeight="bold" noWrap>{room.roomName}</Typography>
                      </Stack>
                      <Chip label={`${room.availableUnits} disp.`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold' }} />
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.4 }}>
                      <Chip icon={<HotelIcon />} label={room.lugar === 'boutique' ? 'Boutique' : 'Casa Hotel'} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', bgcolor: room.lugar === 'boutique' ? '#e8eaf6' : '#fff3e0', color: room.lugar === 'boutique' ? '#283593' : '#e65100' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: '2px !important' }}>{room.roomType}</Typography>
                    </Stack>
                    {room.price > 0 && (() => {
                      const subtotal = room.price;
                      const iva = subtotal * 0.16;
                      const municipal = subtotal * 0.04;
                      const priceWithTax = subtotal + iva + municipal;
                      const totalWithTax = priceWithTax * (msg.nights || 1);
                      return (
                      <Box sx={{ mt: 0.6, p: 0.6, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>{t('chatbot.pricePerNight')}</Typography>
                          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.72rem', color: '#1a1a1a' }}>{formatMXN(priceWithTax)}</Typography>
                        </Stack>
                        {msg.nights > 0 && (
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.2 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>{t('chatbot.totalNights', { nights: msg.nights })}</Typography>
                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.78rem', color: '#d4af37' }}>{formatMXN(totalWithTax)}</Typography>
                          </Stack>
                        )}
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#999', display: 'block', mt: 0.2 }}>{t('chatbot.taxNote')}</Typography>
                        {isConverted() && <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#b8860b', display: 'block', mt: 0.1 }}>{t('currency.approxNote', { currency: getCurrency() })}</Typography>}
                      </Box>
                      );
                    })()}
                    <Button variant="contained" size="small" fullWidth endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                      href={getBookingUrl(room, msg.dates)} target="_blank"
                      sx={{ mt: 0.8, bgcolor: '#d4af37', color: '#fff', textTransform: 'none', fontWeight: 'bold', fontSize: '0.7rem', py: 0.3, borderRadius: '8px', '&:hover': { bgcolor: '#b8960f' } }}>
                      {t('chatbot.bookNow')}
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {msg.unavailableRooms && msg.unavailableRooms.length > 0 && (
                <Box sx={{ mt: 0.3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t('chatbot.notAvailable')}:</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.3} sx={{ mt: 0.2 }}>
                    {msg.unavailableRooms.map((r, ri) => (
                      <Chip key={ri} icon={<CancelIcon />} label={r.roomName} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' }} />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </Box>
        {!isBot && <Avatar sx={{ bgcolor: '#1a1a1a', width: 28, height: 28, mt: 0.3 }}><PersonIcon sx={{ fontSize: 16 }} /></Avatar>}
      </Box>
    );
  };

  /* ─── quick suggestions ─── */
  const buildQuickQuery = (label) => {
    const now = new Date();
    if (label === t('chatbot.quickToday')) {
      return `${now.getDate()} de ${now.toLocaleDateString('es-MX', { month: 'long' })} ${now.getFullYear()}`;
    }
    if (label === t('chatbot.quickWeekend')) {
      const d = now.getDay();
      const fri = new Date(now); fri.setDate(fri.getDate() + (d <= 5 ? 5 - d : 6));
      const sun = new Date(fri); sun.setDate(sun.getDate() + 2);
      return `del ${fri.getDate()} al ${sun.getDate()} de ${fri.toLocaleDateString('es-MX', { month: 'long' })} ${fri.getFullYear()}`;
    }
    return label;
  };

  return (
    <>
      {/* ───── WELCOME OVERLAY ───── */}
      <Backdrop open={showOverlay} sx={{ zIndex: 9999, bgcolor: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)' }}>
        <Fade in={showOverlay} timeout={800}>
          <Paper elevation={24} sx={{
            maxWidth: 420, width: '90%', mx: 'auto', borderRadius: 3, overflow: 'hidden',
            textAlign: 'center', border: '1px solid rgba(212,175,55,.4)',
          }}>
            {/* Gold accent top */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d4af37 0%, #f5e6a3 50%, #d4af37 100%)' }} />

            <Box sx={{ px: 4, py: 4 }}>
              {/* Bot avatar */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <Avatar sx={{
                  width: 64, height: 64, bgcolor: '#1a1a1a',
                  border: '3px solid #d4af37',
                  animation: `${pulse} 2s infinite`,
                }}>
                  <BotIcon sx={{ fontSize: 32, color: '#d4af37' }} />
                </Avatar>
              </Box>

              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#1a1a1a', mb: 1, letterSpacing: '.5px' }}>
                {t('chatbot.overlayTitle')}
              </Typography>

              <Box sx={{ width: 40, height: 2, bgcolor: '#d4af37', mx: 'auto', mb: 2 }} />

              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7, mb: 0.5 }} dangerouslySetInnerHTML={{ __html: t('chatbot.overlayDesc1') }} />
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7, mb: 2.5 }}>
                {t('chatbot.overlayDesc2Prefix')} <strong>{t('chatbot.overlayDesc2Bold')}</strong> <Box component="span" sx={{ color: '#d4af37' }}>●</Box> {t('chatbot.overlayDesc2Suffix')}
              </Typography>

              <Button
                variant="contained"
                onClick={handleAcceptOverlay}
                sx={{
                  bgcolor: '#d4af37', color: '#fff', fontFamily: 'Georgia, serif',
                  textTransform: 'none', fontWeight: 700, fontSize: '1rem',
                  px: 5, py: 1.2, borderRadius: '30px',
                  letterSpacing: '.5px',
                  '&:hover': { bgcolor: '#b8960f' },
                }}
              >
                {t('chatbot.overlayBtn')}
              </Button>
            </Box>

            {/* Gold accent bottom */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d4af37 0%, #f5e6a3 50%, #d4af37 100%)' }} />
          </Paper>
        </Fade>
      </Backdrop>

      {/* ───── FLOATING BUBBLE ───── */}
      <Zoom in={!showOverlay} timeout={500}>
        <Box
          onClick={() => setOpen(prev => !prev)}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 20 : 28,
            right: isMobile ? 20 : 28,
            zIndex: 1300,
            width: 60, height: 60,
            borderRadius: '50%',
            bgcolor: '#d4af37',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(212,175,55,.45)',
            animation: !open ? `${pulse} 2.5s infinite, ${float} 3s ease-in-out infinite` : 'none',
            transition: 'transform .25s ease, background-color .25s ease',
            '&:hover': { transform: 'scale(1.1)', bgcolor: '#c9a430' },
          }}
        >
          {open
            ? <CloseIcon sx={{ fontSize: 28, color: '#fff' }} />
            : <BotIcon sx={{ fontSize: 30, color: '#fff' }} />
          }
        </Box>
      </Zoom>

      {/* ───── Mini tooltip label (when closed) ───── */}
      {!open && !showOverlay && (
        <Fade in timeout={600}>
          <Box sx={{
            position: 'fixed',
            bottom: isMobile ? 86 : 94,
            right: isMobile ? 12 : 20,
            zIndex: 1300,
            bgcolor: '#1a1a1a', color: '#fff',
            px: 1.5, py: 0.6, borderRadius: '10px',
            fontSize: '0.72rem', fontWeight: 600,
            boxShadow: '0 2px 12px rgba(0,0,0,.25)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            '&::after': {
              content: '""', position: 'absolute',
              bottom: -6, right: 24,
              border: '6px solid transparent',
              borderTopColor: '#1a1a1a',
              borderBottom: 'none',
            },
          }}>
            💬 {t('chatbot.tooltip')}
          </Box>
        </Fade>
      )}

      {/* ───── CHAT WINDOW ───── */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper elevation={16} sx={{
          position: 'fixed',
          bottom: isMobile ? 0 : 100,
          right: isMobile ? 0 : 28,
          zIndex: 1299,
          width: isMobile ? '100vw' : 380,
          height: isMobile ? '100vh' : 520,
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          border: isMobile ? 'none' : '1px solid rgba(212,175,55,.35)',
        }}>
          {/* Header */}
          <Box sx={{
            px: 2, py: 1.3,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#d4af37' }}><BotIcon sx={{ fontSize: 18 }} /></Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2, fontFamily: 'Georgia, serif', letterSpacing: '.3px' }}>La Capilla</Typography>
                <Typography variant="caption" sx={{ color: '#d4af37', fontSize: '0.65rem' }}>{t('chatbot.headerSub')}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.3}>
              <Tooltip title={t('chatbot.changeLang')}>
                <IconButton size="small" onClick={(e) => setLangAnchor(e.currentTarget)} sx={{ color: 'rgba(255,255,255,.6)', '&:hover': { color: '#fff' } }}>
                  <Box component="span" sx={{ fontSize: 16 }}>{currentLang.flag}</Box>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={langAnchor}
                open={Boolean(langAnchor)}
                onClose={() => setLangAnchor(null)}
                PaperProps={{ sx: { bgcolor: '#1a1a1a', border: '1px solid rgba(212,175,55,.35)', minWidth: 140 } }}
              >
                {LANGS.map(lang => (
                  <MenuItem
                    key={lang.code}
                    selected={lang.code === currentLang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setLangAnchor(null); }}
                    sx={{
                      color: '#d4af37', fontSize: '0.85rem', gap: 1,
                      '&.Mui-selected': { bgcolor: 'rgba(212,175,55,.15)' },
                      '&:hover': { bgcolor: 'rgba(212,175,55,.1)' },
                    }}
                  >
                    <span>{lang.flag}</span> {lang.label}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title={t('chatbot.clearChat')}>
                <IconButton size="small" onClick={() => setMessages([{ role: 'bot', content: WELCOME_MSG_CONTENT }])} sx={{ color: 'rgba(255,255,255,.6)', '&:hover': { color: '#fff' } }}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'rgba(255,255,255,.6)', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Messages */}
          <Box sx={{
            flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, bgcolor: '#fff',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#d4af37', borderRadius: 3 },
          }}>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </Box>

          {/* Quick suggestions */}
          <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#fafafa', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
            {[t('chatbot.quickToday'), t('chatbot.quickWeekend')].map((s, i) => (
              <Chip key={i} label={s} size="small" onClick={() => setInput(buildQuickQuery(s))}
                sx={{ cursor: 'pointer', bgcolor: '#fff', border: '1px solid #d4af37', color: '#1a1a1a', fontSize: '0.68rem', height: 24, '&:hover': { bgcolor: '#fdf6e3' } }} />
            ))}
          </Box>

          {/* Input */}
          <Box sx={{ p: 1.2, bgcolor: '#fff', borderTop: '1px solid #eee' }}>
            <Stack direction="row" spacing={0.8}>
              <TextField
                inputRef={inputRef}
                fullWidth size="small"
                placeholder={t('chatbot.placeholder')}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px', bgcolor: '#fafafa', fontSize: '0.85rem',
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#d4af37' },
                    '&.Mui-focused fieldset': { borderColor: '#d4af37' },
                  },
                }}
              />
              <IconButton onClick={handleSend} disabled={loading || !input.trim()}
                sx={{ bgcolor: '#d4af37', color: '#fff', width: 38, height: 38, '&:hover': { bgcolor: '#b8960f' }, '&.Mui-disabled': { bgcolor: '#e0e0e0' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      </Slide>
    </>
  );
}
